import SuggestedUsersTextField from "@/components/communications-chat-components/SuggestedUsersTextField";
import { Colors } from "@/enums/Colors";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { useControlNavbarColor } from "@/hooks/useControlNavbarColor";
import settings from "@/images/settings.svg";
import { SmallUserDTO } from "@/models/SmallUserDTO";
import { LazoChatConversationShell, mapToShell } from "@/models/ai-chat/LazoChatConversation";
import { SmallConversationMember } from "@/models/ai-chat/SmallConversationMember";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import { AvatarGroup, Box, SxProps, avatarClasses, avatarGroupClasses, styled } from "@mui/material";
import Image from "next/image";
import React, { Dispatch, SetStateAction } from "react";
import { MentionItem } from "react-mentions";
import { generateFirstChatMessage } from "../ChatBar";
import { PortalChatAvatar } from "./PortalChatAvatar";
import PortalChatBarTitle from "./PortalChatBarTitle";

interface PortalChatBarProps {
  conversation: LazoChatConversationShell;
  setOpenSettings: Dispatch<React.SetStateAction<boolean>>;
  setNewConversationMembers: Dispatch<SetStateAction<string[]>>;
  onChatSelect: (conv: LazoChatConversationShell) => void;
  chats: LazoChatConversationShell[];
  setChatPreview: Dispatch<SetStateAction<LazoChatConversationShell>>;
  members: SmallConversationMember[];
  isMobile?: boolean;
}

function PortalChatBar(props: PortalChatBarProps) {
  const currentUserExternalId = AuthService.getUser()?.externalId;

  const userChattingWith = props.members.filter((member) => member.userExternalId !== currentUserExternalId)[0];

  // Control navbar background color
  useControlNavbarColor(Colors.WHITE);

  function handleMentionsChange(_event: { target: { value: string } }, mentions: MentionItem[], filteredUsers: SmallUserDTO[]) {
    const newConvMembers = mentions.reduce((filtered, item) => {
      if (!filtered.includes(item.id)) {
        filtered.push(item.id);
      }
      return filtered;
    }, []);
    props.setNewConversationMembers(newConvMembers);

    // Show preview while creating conversation
    generateConversationPreview(newConvMembers, filteredUsers);
  }

  function generateConversationPreview(newConvMembers: string[], filteredUsers: SmallUserDTO[]) {
    if (newConvMembers.length === 0) return;

    LazoChatConversationService.findConversationByMembers(newConvMembers).then((r) => {
      r.onSuccess((l) => {
        props.setChatPreview(mapToShell(l));
      });

      r.onError(() => {
        props.setChatPreview({
          messages: [generateFirstChatMessage(filteredUsers)],
          isOwner: true,
          membersCount: filteredUsers.length,
          unreadMessagesCount: 0,
          mentionsCount: 0,
          lastMessageText: "",
        } as LazoChatConversationShell);
      });
    });
  }

  return (
    <>
      <Bar>
        <Container>
          <AvatarContainer>
            <PortalChatAvatar chat={props.conversation} />
            <PortalChatBarTitle userChattingWith={userChattingWith} onChatSelect={props.onChatSelect} conversation={props.conversation} />
          </AvatarContainer>

          <ActionsContainer>
            {props.conversation && (
              <GrayContainer
                onClick={() => props.setOpenSettings(true)}
                sx={{
                  minHeight: { xs: "44px", sm: "2rem" },
                  minWidth: { xs: "44px", sm: "auto" },
                  padding: { xs: "12px", sm: "0.25rem 0.5rem" },
                }}
              >
                <Image
                  width={props.isMobile ? 20 : 16}
                  height={props.isMobile ? 20 : 16}
                  alt="settings-button-conversation-component"
                  src={settings.src}
                />
              </GrayContainer>
            )}
          </ActionsContainer>
        </Container>
      </Bar>
      {!props.conversation && (
        <SuggestedUsersTextField onMentionsChange={handleMentionsChange} conversationExternalId={props.conversation?.externalId} />
      )}
    </>
  );
}

const ActionsContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "1rem",
  position: "relative",
  right: "1rem",
}));

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  padding: "1rem",
  [theme.breakpoints.down("sm")]: {
    padding: "0.75rem 0.5rem",
  },
}));

const Bar = styled(Box)(({ theme }) => ({
  width: "100%",
  position: "sticky",
  height: "3.9rem",
  display: "flex",
  alignItems: "center",
  top: 0,
  backgroundColor: Colors.WHITE,
  zIndex: 2,
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1} `,
  [theme.breakpoints.down("sm")]: {
    height: "4.5rem",
    minHeight: "4.5rem",
  },
}));

const AvatarContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));

const UserAvatarSx: SxProps = {
  width: "20px",
  height: "20px",
  borderRadius: "0.125rem",
  backgroundColor: "#fff",
  backgroundImage: "none",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1} `,
  color: Colors.ELEGANT_BLACK,
  fontWeight: 400,
  fontSize: "0.625rem",
};

const ChatBarUserAvatarSx: SxProps = {
  borderRadius: "0.5rem",
  backgroundColor: "#fff",
  backgroundImage: "none",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1} `,
  color: Colors.ELEGANT_BLACK,
  fontSize: "1.125rem",
};

const GrayContainer = styled(Box)(({ theme }) => ({
  height: "2rem",
  display: "flex",
  padding: "0.25rem 0.5rem",
  gap: "0.5rem",
  alignItems: "center",
  justifyContent: "center",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1} `,
  borderRadius: "0.25rem",
  backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  ":hover": {
    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
    cursor: "pointer",
  },
  [theme.breakpoints.down("sm")]: {
    height: "44px",
    width: "44px",
    minHeight: "44px",
    minWidth: "44px",
    padding: "12px",
  },
}));

const StyledAvatarGroup = styled(AvatarGroup)(() => ({
  display: "flex",
  alignItems: "center",
  [`& .${avatarClasses.root} `]: {
    padding: 0,
    width: "fit-content",
    border: 0,
    borderRadius: 0,
    height: "fit-content",
    backgroundColor: "transparent",
  },
  [`& .${avatarGroupClasses.avatar} `]: {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: Colors.BLACK_151517,
    border: 0,
    backgroundColor: "transparent",
    height: "100%",
    width: "fit-content",
    margin: 0,
    padding: 0,
    marginLeft: "0.5rem",
  },
}));

export default PortalChatBar;
